<script lang="ts" setup>
import type { Podcast } from '~/types/podcasts'
import type { EpisodeListItem } from '~/types/episodes'
import { computed, watch } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'

const { podcast } = defineProps<{
  podcast: Podcast
}>()

const episodeList = ref<EpisodeListItem[]>([])
const searchQuery = ref('')
const query = ref('')
const offset = ref(0)
const limit = 10
const hasMore = ref(true)

const nuxtApp = useNuxtApp()

const { data: episodes, status } = await useFetch(`/api/podcasts/${podcast.identifier}/episodes`, {
  key: `podcast-${podcast.identifier}-episodes-${query.value}-${offset.value}`,
  query: {
    query,
    offset,
    limit,
    sortDir: podcast.showType === 'serial' ? 'asc' : 'desc',
  },
  headers: useRequestHeaders(['x-forwarded-for', 'user-agent']),
  getCachedData: (key) => nuxtApp.payload.data[key] || nuxtApp.static.data[key],
})

if (episodes.value) {
  episodeList.value = episodes.value
}

const isLoading = computed(() => status.value === 'pending')

watch(episodes, (newEpisodes) => {
  if (newEpisodes) {
    episodeList.value = !offset.value ? newEpisodes : [...episodeList.value, ...newEpisodes]
    hasMore.value = newEpisodes.length === limit
  } else {
    hasMore.value = false
  }
})

useInfiniteScroll(
  document,
  async () => {
    if (isLoading.value || !hasMore.value) return
    offset.value += limit
  },
  { distance: 10, interval: 500 }
)

const debouncedFetch = useDebounceFn((searchQuery: string) => {
  offset.value = 0
  hasMore.value = true
  query.value = searchQuery
}, 300)

watch(searchQuery, (newQuery) => {
  debouncedFetch(newQuery)
})

const clearSearch = () => {
  searchQuery.value = ''
  offset.value = 0
  hasMore.value = true
  query.value = ''
}
</script>

<template>
  <div class="space-y-8">
    <div class="flex flex-wrap gap-4 items-center justify-between">
      <div class="relative w-full">
        <div class="absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none">
          <Icon class="mr-2 text-gray-400" name="solar:magnifer-linear" size="16" />
        </div>
        <input
          v-model="searchQuery"
          class="bg-zeno-gray-light border border-zeno-gray-border text-sm rounded-full w-full ps-10 p-2.5 block"
          placeholder="Search episodes"
          type="text"
        />

        <div v-if="isLoading" class="absolute inset-y-0 end-0 flex items-center pr-4">
          <Icon class="animate-spin" name="mingcute:loading-3-fill" size="24" />
        </div>

        <button v-else-if="searchQuery" class="absolute inset-y-0 end-0 flex items-center pr-5" @click="clearSearch">
          <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8 6.586L12.95 1.636 14.364 3.05 9.414 8l4.95 4.95-1.414 1.414L8 9.414l-4.95 4.95L1.636 12.95 6.586 8 1.636 3.05 3.05 1.636 8 6.586z"
              fill="#999999"
            />
          </svg>
        </button>
      </div>
    </div>

    <EpisodeList v-if="episodeList.length" :episodes="episodeList" :podcast="podcast" />

    <Icon v-if="isLoading" class="animate-spin mx-auto mt-4" name="mingcute:loading-3-fill" size="48" />
  </div>
</template>

<style scoped></style>
